import { ExpandMore } from '@mui/icons-material';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Chip,
  Grid,
  InputLabel,
  Typography,
} from '@mui/material';
import { PropsWithChildren } from 'react';

export const AccordionField = ({
  title,
  explainer,
  children,
  inputId,
}: PropsWithChildren<{ title?: string; explainer?: boolean; inputId?: string }>) => {
  const labelStyles = children ? { marginBottom: 0.5 } : { margin: 0 };
  const LabelVariant = () => (
    <InputLabel sx={{ color: 'black', ...labelStyles }} id={`${inputId}-label`}>
      {title}
    </InputLabel>
  );
  const TextVariant = () => (
    <Typography variant={explainer ? 'explainer' : undefined} sx={labelStyles}>
      {title}
    </Typography>
  );
  return (
    <Grid sx={{ display: 'grid', width: '100%' }}>
      {/* eslint-disable-next-line react-hooks/static-components -- label variants are local to this accordion field */}
      {title !== undefined ? inputId ? <LabelVariant /> : <TextVariant /> : <></>}
      {children}
    </Grid>
  );
};
interface AccordionFieldProps extends PropsWithChildren {
  headerText: string;
  expanded?: boolean;
  defaultExpanded?: boolean;
  onChange?: (event: React.SyntheticEvent, expanded: boolean) => void;
  largeTitle?: boolean;
  disabled?: boolean;
  inUse?: boolean;
  color?: string;
}
export const fixTopBorderStyling = {
  '::before': {
    opacity: 0,
  },
  borderTop: '1px solid rgba(0, 0, 0, 0.12)',
};
const DrawerAccordion = ({
  headerText,
  children,
  defaultExpanded,
  expanded,
  onChange,
  inUse,
  disabled = false,
  color,
  largeTitle,
}: AccordionFieldProps) => {
  const accordionSummaryStyle = { paddingLeft: 2, fontWeight: '700' };
  return (
    <Accordion
      disableGutters
      sx={{
        ...fixTopBorderStyling,
        boxShadow: 'none',
        ':first-of-type': { borderRadius: 0 },
        ':last-of-type': { borderRadius: 0 },
      }}
      slotProps={{ transition: { unmountOnExit: true } }}
      defaultExpanded={defaultExpanded}
      expanded={expanded}
      onChange={onChange}
      disabled={disabled}
    >
      <AccordionSummary
        sx={{
          '.MuiAccordionSummary-content': {
            justifyContent: 'space-between',
            marginRight: 2,
            alignContent: 'center',
            color,
          },
          ...accordionSummaryStyle,
        }}
        expandIcon={<ExpandMore />}
      >
        {largeTitle ? (
          <Typography
            component="h3"
            sx={{ fontWeight: '500', fontSize: '1.25rem', lineHeight: 1.6 }}
          >
            {headerText}
          </Typography>
        ) : (
          <Typography component="span" sx={{ fontWeight: '700' }}>
            {headerText}
          </Typography>
        )}
        {inUse && (
          <Chip
            label={'In use'}
            color={'primary'}
            size={'small'}
            variant={'filled'}
            sx={{ fontWeight: '400' }}
          />
        )}
      </AccordionSummary>

      <AccordionDetails sx={{ paddingLeft: 2 }}>
        <Grid sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
          {children}
        </Grid>
      </AccordionDetails>
    </Accordion>
  );
};

export default DrawerAccordion;
